import React, { useState, useEffect, useCallback } from 'react';
import subscriptionPlanClassService from '../services/subscriptionPlanClassService';
import { classeService } from '../services/eleveService';
import { toast } from 'react-toastify';

const PlanClassesManager = ({ plan, isVisible, onClose }) => {
  const [availableClasses, setAvailableClasses] = useState([]);
  const [planClasses, setPlanClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const planSlug = plan?.slug;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Charger toutes les classes disponibles
      const allClassesResponse = await classeService.getAll();
      
      if (allClassesResponse) {
        // Utiliser la même structure que dans StudentProgressionSelector
        const classesData = allClassesResponse.data || [];
        setAvailableClasses(classesData);
      }

      // Charger les classes déjà associées au plan
      const planClassesResponse = await subscriptionPlanClassService.getClasses(planSlug);
      console.log('Plan classes response:', planClassesResponse);
      if (planClassesResponse.success) {
        // Les classes sont maintenant directement dans la data
        console.log('Plan classes data:', planClassesResponse.data);
        setPlanClasses(planClassesResponse.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [planSlug]);

  useEffect(() => {
    if (isVisible && planSlug) {
      loadData();
    }
  }, [isVisible, planSlug, loadData]);

  const handleAddClasses = async () => {
    console.log('Classes sélectionnées à ajouter:', selectedClasses);
    if (selectedClasses.length === 0) {
      toast.warning('Veuillez sélectionner au moins une classe');
      return;
    }

    console.log('Tentative d\'ajout de classes:', selectedClasses);
    console.log('Plan slug:', planSlug);

    setLoadingAction(true);
    try {
      const response = await subscriptionPlanClassService.addClasses(planSlug, selectedClasses);
      console.log('Réponse de l\'ajout:', response);
      
      if (response.success) {
        toast.success(response.message);
        setSelectedClasses([]);
        loadData(); // Recharger les données
      } else {
        console.error('Erreur dans la réponse:', response);
        toast.error(response.message || 'Erreur lors de l\'ajout des classes');
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout des classes:', error);
      toast.error('Erreur lors de l\'ajout des classes');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleRemoveClass = async (classeId) => {
    if (window.confirm('Êtes-vous sûr de vouloir retirer cette classe du plan ?')) {
      setLoadingAction(true);
      try {
        const response = await subscriptionPlanClassService.removeClass(planSlug, classeId);
        if (response.success) {
          toast.success(response.message);
          loadData(); // Recharger les données
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de la classe:', error);
        toast.error('Erreur lors de la suppression de la classe');
      } finally {
        setLoadingAction(false);
      }
    }
  };

  const handleClassSelection = (classeId) => {
    setSelectedClasses(prev => {
      if (prev.includes(classeId)) {
        return prev.filter(id => id !== classeId);
      } else {
        return [...prev, classeId];
      }
    });
  };

  const filteredAvailableClasses = availableClasses.filter(classe => {
    const notInPlan = !planClasses.some(pc => pc.id === classe.id);
    const matchesSearch = !searchTerm || 
                         (classe.label && classe.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (classe.description && classe.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return notInPlan && matchesSearch;
  });

  if (!isVisible) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-collection me-2"></i>
              Gestion des Classes du Plan
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body p-4">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Chargement...</span>
                </div>
              </div>
            ) : (
              <div className="row">
                {/* Classes déjà associées au plan */}
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-primary text-white">
                      <h6 className="card-title mb-0">
                        <i className="bi bi-check-circle me-2"></i>
                        Classes Associées ({planClasses.length})
                      </h6>
                    </div>
                    <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {planClasses.length === 0 ? (
                        <div className="text-center text-muted py-3">
                          <i className="bi bi-inbox display-4"></i>
                          <p className="mt-2">Aucune classe associée</p>
                        </div>
                      ) : (
                        <div className="list-group list-group-flush">
                          {planClasses.map((classe) => (
                            <div key={classe.id} className="list-group-item d-flex justify-content-between align-items-center">
                              <div>
                                <strong>{classe.label}</strong>
                                <br />
                                <small className="text-muted">{classe.description}</small>
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveClass(classe.id)}
                                disabled={loadingAction}
                              >
                                <i className="bi bi-x-circle"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Classes disponibles à ajouter */}
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-success text-white">
                      <h6 className="card-title mb-0">
                        <i className="bi bi-plus-circle me-2"></i>
                        Classes Disponibles
                      </h6>
                    </div>
                    <div className="card-body">
                      {/* Barre de recherche */}
                      <div className="mb-3">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Rechercher une classe..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {filteredAvailableClasses.length === 0 ? (
                          <div className="text-center text-muted py-3">
                            <i className="bi bi-search display-4"></i>
                            <p className="mt-2">Aucune classe disponible</p>
                          </div>
                        ) : (
                          <div className="list-group list-group-flush">
                            {filteredAvailableClasses.map((classe) => (
                              <div key={classe.id} className="list-group-item">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`classe-${classe.id}`}
                                    checked={selectedClasses.includes(classe.id)}
                                    onChange={() => handleClassSelection(classe.id)}
                                  />
                                  <label className="form-check-label" htmlFor={`classe-${classe.id}`}>
                                    <strong>{classe.label}</strong>
                                    <br />
                                    <small className="text-muted">{classe.description}</small>
                                  </label>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {selectedClasses.length > 0 && (
                        <div className="mt-3">
                          <button
                            type="button"
                            className="btn btn-success w-100"
                            onClick={handleAddClasses}
                            disabled={loadingAction}
                          >
                            {loadingAction ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Ajout en cours...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-plus-circle me-2"></i>
                                Ajouter {selectedClasses.length} classe(s)
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanClassesManager;
